import { useMemo } from 'react';

import { hasOutdatedKentroller } from '@cast/split-me/features/kent';
import { useGetFeaturesCompatibilityQuery } from '@cast/split-me/hooks/queries/kent';
import { Cluster } from '@cast/types';

export type KentrollerCompatibilityWithContinuousRebalancingState =
  | { type: 'checking' }
  | {
      type: 'check-error';
      error: unknown;
      retry: () => void;
    }
  | {
      type: 'compatible';
    }
  | {
      type: 'incompatible';
    };

export const useKentrollerCompatabilityWithContinuousRebalancing = ({
  cluster,
  enabled,
}: {
  cluster: Cluster;
  enabled: boolean;
}): KentrollerCompatibilityWithContinuousRebalancingState => {
  const {
    data: featuresCompatibility,
    isLoading,
    error,
    refetch,
  } = useGetFeaturesCompatibilityQuery({
    params: { clusterId: cluster.id },
    options: {
      enabled,
    },
  });

  return useMemo(() => {
    if (isLoading) {
      return { type: 'checking' };
    }

    if (error) {
      return {
        type: 'check-error',
        error,
        retry: refetch,
      };
    }

    const isKontrollerOutdated = hasOutdatedKentroller(
      featuresCompatibility?.features ?? []
    );
    if (isKontrollerOutdated) {
      return { type: 'incompatible' };
    }

    return { type: 'compatible' };
  }, [isLoading, error, refetch, featuresCompatibility?.features]);
};
